import type { InvoiceTotals, LineInput, VatCategory, VatCategoryCode } from '@financiar/shared';
import { computeInvoiceTotals } from '@financiar/shared';
import type { VatRate } from '../../api/types';

/**
 * The editor's LIVE totals — the same numbers the server will store, computed
 * in the browser on every keystroke.
 *
 * "The same" is meant literally: the arithmetic is `computeInvoiceTotals`
 * from `@financiar/shared`, the exact function `InvoicesService`'s
 * `buildDraftLines()` calls, and the (percent, code) pair fed into it is
 * resolved by the mirror of `resolveVatByCategory()` below. Nothing here
 * re-implements money math, and nothing here touches a JS `number` — money
 * and quantities stay decimal strings from the input control all the way to
 * the request body.
 */

export const VAT_CATEGORY_LABELS: Record<VatCategory, string> = {
  STANDARD: 'Standard',
  REDUCED: 'Redusă',
  SUPER_REDUCED: 'Redusă (super)',
};

export const VAT_CATEGORY_ORDER: VatCategory[] = ['STANDARD', 'REDUCED', 'SUPER_REDUCED'];

/** What a non-VAT-payer's lines carry — byte-identical to the API's
 * `NON_VAT_PAYER_VAT` (invoice-vat.util.ts): 0%, EN 16931 category 'O'
 * ("not subject to VAT", the art. 310 small-business exemption). */
const NON_VAT_PAYER_VAT: ResolvedVat = { vatRatePercent: '0.00', vatCategoryCode: 'O' };

export interface ResolvedVat {
  vatRatePercent: string;
  vatCategoryCode: VatCategoryCode;
}

/** Resolves a category to its (percent, code) pair, or `null` when no rate
 * applies today — mirroring `resolveVatByCategory()`:
 *  - firm is not a VAT payer today -> every category collapses to 0.00/'O'
 *    and `/vat-rates` is never consulted;
 *  - firm IS a payer -> today's rate for that category, code 'S';
 *  - no applicable rate -> `null`, which the caller surfaces as "totals not
 *    computable" rather than guessing 0% (the server answers the same case
 *    with a hard 409 VAT_RATE_NOT_FOUND).
 *
 * `ratePercent` off `/vat-rates` is already the 2dp string the server builds
 * with `row.ratePercent.toFixed(2)`, so the value fed to the shared math is
 * character-for-character what the server feeds it.
 */
export type VatResolver = (category: VatCategory) => ResolvedVat | null;

export function makeVatResolver(isVatPayerToday: boolean, vatRates: VatRate[]): VatResolver {
  const rateByCategory = new Map(vatRates.map((rate) => [rate.category, rate.ratePercent]));
  return (category) => {
    if (!isVatPayerToday) return NON_VAT_PAYER_VAT;
    const ratePercent = rateByCategory.get(category);
    return ratePercent ? { vatRatePercent: ratePercent, vatCategoryCode: 'S' } : null;
  };
}

/** A line as the editor's `Form.List` holds it: every field optional, because
 * a freshly added row is empty until the user fills it in. */
export interface LineFormValue {
  productId?: string;
  description?: string;
  quantity?: string | null;
  unitCode?: string;
  unitPrice?: string | null;
  vatCategory?: VatCategory;
}

/** A plain decimal string, or "0" for anything a half-typed row can hold
 * (`null`, `''`, a lone "-"). Deliberately NOT `Number(...) || 0`: the value
 * is passed straight into decimal.js, so it must stay a string. */
const DECIMAL_STRING = /^-?\d+(\.\d+)?$/;

function decimalOrZero(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  return DECIMAL_STRING.test(trimmed) ? trimmed : '0';
}

export type LiveTotals =
  | { status: 'ok'; totals: InvoiceTotals }
  /** No lines at all — `computeInvoiceTotals` throws EMPTY_INVOICE on this,
   * and an invoice with zero lines can't be saved anyway. */
  | { status: 'empty' }
  /** `/vat-rates` hasn't answered yet (`vatRatesLoading` — see
   * `computeLiveTotals`'s param) AND at least one line's category can't be
   * resolved without it. Distinct from `'unresolved-vat'` on purpose: on a
   * cold load there is no rate data yet for ANY category, which would
   * otherwise look identical to a genuinely-missing rate and flash the
   * "fără cotă valabilă" alarm at every VAT-payer draft for a frame. */
  | { status: 'loading' }
  /** Rates HAVE loaded, and at least one line's VAT category still has no
   * rate in effect today; the server would reject the save with
   * VAT_RATE_NOT_FOUND. */
  | { status: 'unresolved-vat' };

/**
 * Totals for the current form state. Every line is included (in order), so
 * `totals.lines[i]` lines up with `Form.List` index `i` and can drive the
 * read-only Valoare/TVA cells.
 *
 * `vatRatesLoading` should be `true` exactly while the caller's `/vat-rates`
 * query is still in flight (`vatRates === undefined`) — it only changes the
 * outcome when `resolveVat` actually fails to resolve a line's category
 * (which, for a non-VAT-payer resolver, never happens regardless of
 * `/vat-rates`'s state, since `makeVatResolver` short-circuits to the fixed
 * 0%/'O' pair before ever consulting the rates list). Defaults to `false` so
 * an existing caller that doesn't pass it keeps its current behavior.
 */
export function computeLiveTotals(
  lines: LineFormValue[] | undefined,
  resolveVat: VatResolver,
  vatRatesLoading = false,
): LiveTotals {
  if (!lines || lines.length === 0) return { status: 'empty' };

  const inputs: LineInput[] = [];
  for (const line of lines) {
    const vat = resolveVat(line?.vatCategory ?? 'STANDARD');
    if (!vat) return vatRatesLoading ? { status: 'loading' } : { status: 'unresolved-vat' };
    inputs.push({
      quantity: decimalOrZero(line?.quantity),
      unitPrice: decimalOrZero(line?.unitPrice),
      vatRatePercent: vat.vatRatePercent,
      vatCategoryCode: vat.vatCategoryCode,
    });
  }

  return { status: 'ok', totals: computeInvoiceTotals(inputs) };
}
